import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  Checklist as ChecklistIcon,
  Badge as SamlIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getSamlACSURL, getSamlSPEntityID, getSamlSPDescriptorURL } from '../../utils/constants';

// SAML 2.0 accent color - Violet
const accentColor = '#7B5EA7';
const accentColorLight = '#F1ECF9';
const accentColorHover = '#674C8C';

/**
 * The copy control itself: tooltip, tick-for-2s swap, accessible name, and
 * disabled-when-there-is-nothing-to-copy.
 *
 * Extracted from CopyRow because three panels need this behaviour but only two of them can
 * use a whole CopyRow — the SAML info strip in CustomApplicationDetailView is an inline row
 * that also carries a Download XML button, and the OIDC client secret sits beside Reveal
 * and Regenerate. Chrome is the caller's (`sx` on the button, `iconSx` on the icon); the
 * behaviour is not, so a copy button cannot end up without a name or without its tick.
 *
 * The `<span>` is load-bearing: MUI's Tooltip needs a non-disabled child to attach its
 * listeners to, and `aria-label` stays on the button so the row is reachable by name.
 *
 * @param {string} label    - what is being copied, e.g. "Client ID". Names the button.
 * @param {string} value    - the value; falsy disables the button
 * @param {boolean} copied  - true while this is the panel's most recently copied control
 * @param {Function} onCopy - invoked with NO arguments (see CopyRow's contract below)
 * @param {object} sx       - styles for the IconButton
 * @param {object} iconSx   - styles for the icon inside it
 */
export const CopyIconButton = ({ label, value, copied, onCopy, sx, iconSx }) => (
  <Tooltip title={copied ? 'Copied!' : `Copy ${label}`}>
    <span>
      <IconButton onClick={onCopy} disabled={!value} size="small" aria-label={`Copy ${label}`} sx={sx}>
        {copied ? (
          <CheckCircleIcon fontSize="small" sx={iconSx} />
        ) : (
          <ContentCopyIcon fontSize="small" sx={iconSx} />
        )}
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * One labelled, copyable connection value.
 *
 * Lifted from CustomIdPList.js's inline "Redirect URI" row and generalised: that
 * version hard-coded a single `copiedRedirectURI` boolean because it only ever
 * rendered one row.
 *
 * CONTRACT — this component is stateless and holds no clipboard logic of its own,
 * so an owner that does not meet all three points below will render a row whose
 * tick never appears or never clears:
 *
 *   1. Keep ONE `copiedKey` string for the whole panel, not a boolean per row.
 *   2. Pass `copied={copiedKey === key}`, so only the row actually clicked ticks.
 *   3. Pass `onCopy={() => handleCopy(key, value)}` — `onCopy` is invoked with NO
 *      arguments. `handleCopy` writes to the clipboard, sets `copiedKey`, and
 *      clears it after ~2s (compare-and-clear, so a later row does not cancel it).
 *
 * `SamlConnectionDetails.handleCopy` below is the reference implementation.
 *
 * Three owners import this: SamlConnectionDetails (here), SamlIdPList, and
 * CustomApplicationDetailView via OidcConnectionDetails — hence a contract stated
 * as a requirement rather than as a description of this file.
 *
 * @param {string} label   - the name the far side uses for this value
 * @param {string} hint    - one line of "where does this go" guidance
 * @param {string} value   - the value itself, rendered monospace and wrapped
 * @param {boolean} copied - true while this row is the most recently copied one
 * @param {Function} onCopy - invoked with no arguments when the copy button is hit
 * @param {boolean} external - also render an "open in a new tab" button (only
 *   meaningful for values that are fetchable URLs, i.e. the SP descriptor)
 */
export const CopyRow = ({ label, hint, value, copied, onCopy, external = false }) => (
  <Box
    sx={{
      backgroundColor: '#FFFFFF',
      border: '1px solid #E5E5E5',
      borderRadius: '6px',
      p: 2,
      mb: 1.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: '#999999',
          fontFamily: 'Metropolis, sans-serif',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: '#253E66',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          wordBreak: 'break-all',
          mt: 0.5,
        }}
      >
        {value || '--'}
      </Typography>
      {hint && (
        <Typography
          variant="caption"
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            display: 'block',
            mt: 0.75,
          }}
        >
          {hint}
        </Typography>
      )}
    </Box>

    <Box sx={{ display: 'flex', gap: 1 }}>
      {external && value && (
        <Tooltip title="Open in a new tab">
          <IconButton
            component="a"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{
              color: accentColor,
              backgroundColor: accentColorLight,
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#E4DAF2',
              },
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <CopyIconButton
        label={label}
        value={value}
        copied={copied}
        onCopy={onCopy}
        sx={{
          color: copied ? '#27AE60' : accentColor,
          backgroundColor: copied ? '#E8F8F0' : accentColorLight,
          border: '1px solid #E5E5E5',
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: copied ? '#D4EFDF' : '#E4DAF2',
          },
        }}
      />
    </Box>
  </Box>
);

/*
 * The Entra-side steps that are not visible from Prism and that each produce a
 * failure the admin would otherwise have to decode from an AADSTS code alone.
 */
const ENTRA_CHECKLIST = [
  'Assign the users (or groups) who should be able to sign in, under Users and groups on the enterprise application. Entra refuses an unassigned user with AADSTS50105 even though the configuration is correct.',
  'Confirm the emailaddress claim is present and populated from user.mail. Prism matches a federated login to an existing account by email, so a user whose mailbox attribute is empty cannot be linked.',
  'Paste the Reply URL exactly as shown. Entra matches it literally: a difference in case or a trailing slash produces AADSTS50011.',
  'Do not use Entra’s "Test single sign-on" button or the MyApps launcher. Both start an IdP-initiated sign-in, which is not supported in this release; start from the Prism login page instead.',
  'Single logout is not configured. Signing out of Prism ends the Prism session only — the Entra session stays live until it expires or the user signs out there.',
];

/**
 * The post-create handoff.
 *
 * Creating the identity provider in Prism is only half the job: nothing works
 * until the three Service Provider values below have been pasted into the
 * customer's identity provider. This dialog is the moment that happens, which is
 * why it cannot be dismissed with Escape or a backdrop click on the create path.
 *
 * The alias is minted server-side and GenerateUniqueAlias appends -1/-2 on
 * collision, so the values the create dialog previewed may not be the real ones.
 * `idp.serviceProvider` is built by the backend from KEYCLOAK_DOMAIN and is
 * authoritative; the constants helpers are only a fallback for an older backend
 * that does not send it.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {object} idp - a SAMLIdPResponse: { alias, displayName, config, serviceProvider }
 * @param {boolean} justCreated - true immediately after a create, false when the
 *   dialog is opened later from the list's "Connection details" action
 */
const SamlConnectionDetails = ({ open, onClose, idp, justCreated = false }) => {
  const [copiedKey, setCopiedKey] = useState(null);

  const alias = idp?.alias || '';
  const serviceProvider = idp?.serviceProvider || {};

  // Backend values win. The helpers below rebuild the same URLs from the
  // browser's view of the Keycloak host, which is right in every deployment we
  // ship but is a guess, not a fact.
  const acsUrl = serviceProvider.acsUrl || getSamlACSURL(alias);
  const spEntityId = serviceProvider.entityId || getSamlSPEntityID();
  const descriptorUrl = serviceProvider.metadataDescriptorUrl || getSamlSPDescriptorURL(alias);

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCopyAll = () => {
    const block = [
      `Reply URL (Assertion Consumer Service URL): ${acsUrl}`,
      `Identifier (Entity ID): ${spEntityId}`,
      `SP metadata URL: ${descriptorUrl}`,
    ].join('\n');
    handleCopy('all', block);
  };

  // While justCreated the admin has not yet taken the values away, so the two
  // accidental dismissals are refused. The explicit Done button always works.
  const handleDialogClose = (event, reason) => {
    if (justCreated && (reason === 'escapeKeyDown' || reason === 'backdropClick')) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={justCreated}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          color: '#253E66',
          borderBottom: '1px solid #E5E5E5',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <SamlIcon sx={{ color: accentColor }} />
        {justCreated ? 'Finish setup at your identity provider' : 'Connection details'}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {justCreated && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              backgroundColor: '#E8F8F0',
              border: '1px solid #27AE6020',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#27AE60' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            Sign-in will not work until you paste the values below into your identity provider.
          </Alert>
        )}

        {serviceProvider.warning && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              backgroundColor: '#FEF9E7',
              border: '1px solid #F39C1220',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#F39C12' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            {serviceProvider.warning}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            backgroundColor: accentColorLight,
            border: `1px solid ${accentColor}20`,
            borderRadius: '8px',
            p: 2.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: accentColor,
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                Service Provider values
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#253E66',
                  fontFamily: 'Metropolis, sans-serif',
                  mt: 0.5,
                }}
              >
                Alias:{' '}
                <span style={{ fontFamily: 'monospace' }}>{alias || '--'}</span>
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={handleCopyAll}
              startIcon={
                copiedKey === 'all' ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                borderColor: copiedKey === 'all' ? '#27AE60' : '#E5E5E5',
                color: copiedKey === 'all' ? '#27AE60' : accentColor,
                backgroundColor: '#FFFFFF',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: copiedKey === 'all' ? '#27AE60' : accentColor,
                  backgroundColor: accentColorLight,
                },
              }}
            >
              {copiedKey === 'all' ? 'Copied' : 'Copy all'}
            </Button>
          </Box>

          <CopyRow
            label="Reply URL (Assertion Consumer Service URL)"
            hint="Entra ID: Single sign-on → Basic SAML Configuration → Reply URL. Matching is case- and trailing-slash-sensitive."
            value={acsUrl}
            copied={copiedKey === 'acsUrl'}
            onCopy={() => handleCopy('acsUrl', acsUrl)}
          />

          <CopyRow
            label="Identifier (Entity ID)"
            hint="Entra ID: Single sign-on → Basic SAML Configuration → Identifier (Entity ID). Shared by every SAML provider in this realm."
            value={spEntityId}
            copied={copiedKey === 'spEntityId'}
            onCopy={() => handleCopy('spEntityId', spEntityId)}
          />

          <CopyRow
            label="SP metadata URL"
            hint="Public, unauthenticated, and only resolves once the provider has been saved. Identity providers that import Service Provider metadata can read everything above from it."
            value={descriptorUrl}
            copied={copiedKey === 'descriptorUrl'}
            onCopy={() => handleCopy('descriptorUrl', descriptorUrl)}
            external
          />
        </Paper>

        <Accordion
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': { margin: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#999999' }} />}
            sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 2.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 30, height: 30, backgroundColor: accentColorLight }}>
                <ChecklistIcon sx={{ fontSize: 16, color: accentColor }} />
              </Avatar>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: '#253E66',
                }}
              >
                Microsoft Entra ID checklist
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 1.5, borderColor: '#F0F0F0' }} />
            <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {ENTRA_CHECKLIST.map((item) => (
                <Typography
                  key={item}
                  component="li"
                  variant="body2"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#666666',
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E5E5E5' }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: accentColor,
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            px: 3,
            '&:hover': {
              backgroundColor: accentColorHover,
            },
          }}
        >
          {justCreated ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SamlConnectionDetails;
